import { ConversionPermissions } from "@redotech/account-common/permissions";
import { useRequiredContext } from "@redotech/react-util/context";
import { useTranslation } from "@redotech/redo-i18n";
import CONVERSION_ICON from "@redotech/redo-icons/shopping-cart-03.svg";
import CONVERSION_ICON_FILLED from "@redotech/redo-icons/shopping-cart-03_filled.svg";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { useCapability } from "@redotech/redo-merchant-app-common/use-capability";
import { LinkPrimaryTab } from "@redotech/redo-web/arbiter-components/navigation/primary/redo-primary-nav";
import { PageNavigationEntry } from "@redotech/redo-web/arbiter-components/navigation/redo-side-navigation";
import { SecondaryNavigationData } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav";
import { useNavTreeNode } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { memo } from "react";
import { ConversionAccessContext } from "../../../../conversion/context/conversion-access";
import { IntentOnlySidebarPromo } from "../../../../conversion/locked/intent-only-sidebar-promo";
import { AbTestItems } from "./ab-tests";
import { AnalyticsItems } from "./analytics";
import { CheckoutItems } from "./checkouts";
import { DashboardItems } from "./dashboard";
import { DiscountRulesItems } from "./discount-rules";
import { MarketsItems } from "./markets";
import { PopupsItems } from "./popups";
import { PostPurchaseItems } from "./post-purchase";
import { PricingTestItems } from "./pricing-tests";
import { WidgetsItems } from "./widgets";

export function useConversionNavigation(): PageNavigationEntry | null {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const team = useRequiredContext(TeamContext);
  const { checkoutOutboundRatesEnabled, hasConversionSecondaryNav } =
    useRequiredContext(ConversionAccessContext);
  const checkoutOptimizationEnabled = useCapability("checkoutOptimization");

  const canViewConversion = useHasPermission(
    ConversionPermissions.ACCESS_CONVERSION,
  );

  if (team.platform !== "shopify" || !canViewConversion) {
    return null;
  }

  const pathRoot = routes.conversionBaseUrl;

  // Conversion off → welcome landing. Conversion on → dashboard / onboard via
  // the existing helper. Predictive alone does not open the suite.
  const conversionHref = routes.getConversionHref({
    enabled: checkoutOptimizationEnabled,
    checkoutOutboundRatesEnabled,
  });

  const primaryTab: LinkPrimaryTab = {
    id: "conversion",
    hasNotification: false,
    UnfilledIcon: CONVERSION_ICON,
    FilledIcon: CONVERSION_ICON_FILLED,
    type: "link",
    href: conversionHref,
    pathRoot,
    friendlyName: t("Conversion", {
      context:
        "primary navigation section for checkout conversion optimization",
    }),
  };

  const secondaryNavigationData: SecondaryNavigationData | undefined =
    hasConversionSecondaryNav
      ? {
          id: "conversion",
          name: t("Conversion", {
            context:
              "primary navigation section for checkout conversion optimization",
          }),
          NavContent: SecondaryConversionSection,
        }
      : undefined;

  return {
    primaryTab,
    secondaryNavigationData,
    isActiveProduct: hasConversionSecondaryNav,
  };
}

const SecondaryConversionSection = memo(function SecondaryConversionSection() {
  const { t } = useTranslation();
  const { id: nodeId, TreeContext } = useNavTreeNode({
    friendlyName: t("Conversion", {
      context:
        "primary navigation section for checkout conversion optimization",
    }),
  });
  const { isIntentFocusedDashboard } = useRequiredContext(
    ConversionAccessContext,
  );

  // Partial tier (locks present): promote Spotlights next to Dashboard.
  // Full unlocked suite keeps historical order. Items self-hide when
  // locked + presentation is "hidden" (under $3k). Promo visibility is
  // owned by IntentOnlySidebarPromo's internal locks/presentation check.
  return (
    <TreeContext.Provider value={nodeId}>
      {isIntentFocusedDashboard ? (
        <>
          <DashboardItems />
          <PopupsItems />
          <AbTestItems />
          <AnalyticsItems />
          <CheckoutItems />
          <DiscountRulesItems />
          <PostPurchaseItems />
          <PricingTestItems />
          <WidgetsItems />
          <MarketsItems />
          <IntentOnlySidebarPromo />
        </>
      ) : (
        <>
          <DashboardItems />
          <AbTestItems />
          <AnalyticsItems />
          <CheckoutItems />
          <DiscountRulesItems />
          <PostPurchaseItems />
          <PricingTestItems />
          <PopupsItems />
          <WidgetsItems />
          <MarketsItems />
          <IntentOnlySidebarPromo />
        </>
      )}
    </TreeContext.Provider>
  );
});
